<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-support-cours-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="supportForm" @submit="addSupportCours" @invalid-submit="handleInvalidSubmit"
        :validation-schema="supportSchema" :validateOnInput="false" :validateOnMount="false" :initialValues="{
          nombreHeures: '',
          prerequis: '',
          description: '',
          titre: '',
          auteur: '',
          fichier: null,
          niveau: null,
          filiere: null,
          matiere: null,
          estModulaire: false,
          modulesAdded: []
        }">

        <!-- Ligne 1-2-3 : Champs + Image à droite -->
        <div class="row gx-2">

          <!-- Colonne gauche : tous les champs -->
          <div class="col-md-10">

            <!-- Ligne 1 : Titre + Niveau + Filière -->
            <div class="row">
              <!-- Titre -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Titre <span class="text-danger">*</span>
                  </label>
                  <Field name="titre" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Titre du support" />
                  <ErrorMessage name="titre" class="text-danger" />
                </div>
              </div>

              <!-- Niveau -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Niveau <span class="text-danger">*</span>
                  </label>
                  <Field name="niveau" v-slot="{ field }">
                    <Multiselect :options="niveauxOptions" :searchable="true" track-by="value" label="label"
                      v-bind="field" @change="onNiveauChange" placeholder="Sélectionner le niveau"
                      :disabled="isLoadingAssignments" />
                  </Field>
                  <ErrorMessage name="niveau" class="text-danger" />
                </div>
              </div>

              <!-- Filière -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Filière <span class="text-danger">*</span>
                  </label>
                  <Field name="filiere" v-slot="{ field }">
                    <Multiselect :options="filieresOptions" :searchable="true" track-by="value" label="label"
                      v-bind="field" @change="onFiliereChange" placeholder="Sélectionner la filière"
                      :disabled="!selectedNiveau || isLoadingFilieres" />
                  </Field>
                  <ErrorMessage name="filiere" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- Ligne 2 : Matière + Nombre d'heures + Auteur -->
            <div class="row">
              <!-- Matière -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Matière <span class="text-danger">*</span>
                  </label>
                  <Field name="matiere" v-slot="{ field }">
                    <Multiselect :options="matieresOptions" :searchable="true" track-by="value" label="label"
                      v-bind="field" placeholder="Sélectionner la matière"
                      :disabled="!selectedNiveau || !selectedFiliere || isLoadingMatieres" />
                  </Field>
                  <ErrorMessage name="matiere" class="text-danger" />
                </div>
              </div>

              <!-- Nombre d'heures -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Nombre d'heures <span class="text-danger">*</span>
                  </label>
                  <Field name="nombreHeures" type="number" min="1" max="1000"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 60" />
                  <ErrorMessage name="nombreHeures" class="text-danger" />
                </div>
              </div>

              <!-- Auteur -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">Auteur</label>
                  <Field name="auteur" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Nom de l'auteur (facultatif)" />
                  <ErrorMessage name="auteur" class="text-danger" />
                </div>
              </div>
            </div>

            <!-- Ligne 3 : Fichier + Prérequis + Description -->
            <div class="row">
              <!-- Fichier -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Fichier principal (PDF) <span class="text-danger">*</span>
                  </label>
                  <Field name="fichier" type="file" accept="application/pdf" @change="onFichierChange"
                    class="form-control shadow-none fs-md-15 text-black" />
                  <ErrorMessage name="fichier" class="text-danger" />
                  <small v-if="fichierName" class="text-success d-block mt-1">
                    {{ fichierName }}
                  </small>
                </div>
              </div>

              <!-- Prérequis -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">Prérequis</label>
                  <Field name="prerequis" as="textarea" rows="4" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Connaissances requises pour ce cours..." />
                  <ErrorMessage name="prerequis" class="text-danger" />
                </div>
              </div>

              <!-- Description -->
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">Description</label>
                  <Field name="description" as="textarea" rows="4" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Description détaillée du support de cours..." />
                  <ErrorMessage name="description" class="text-danger" />
                </div>
              </div>
            </div>

          </div>
          <!-- fin col-md-10 -->
          <!-- Colonne droite : Image de couverture -->
          <!-- Colonne droite : Image de couverture -->
          <div class="col-md-2">
            <label class="d-block text-black fw-semibold mb-1">
              Image de couverture
            </label>
            <div class="upload-container">
              <div class="preview-card" @click="triggerFileInput">
                <img v-if="imagePreview" :src="imagePreview" alt="Image preview" />
                <div v-else class="text-center">
                  <i class="icofont icofont-camera fs-1"></i>
                  <div class="mt-2">Cliquez pour ajouter l'image</div>
                </div>
                <button v-if="imagePreview" class="delete-btn" @click.stop="removeImage">
                  <i class="icofont icofont-close"></i>
                </button>
              </div>

              <!-- Input caché -->
              <input ref="fileInput" type="file" accept="image/jpeg, image/png, image/jpg, image/webp" hidden
                @change="onFilePrinChange" />

              <ErrorMessage name="urlImage" class="text-danger" />
            </div>
          </div>
          <!-- fin col-md-2 image -->

        </div>
        <!-- Checkbox en bas -->
        <div class="row mt-4">
          <div class="col-md-12">
            <div class="form-check">
              <Field name="estModulaire" type="checkbox" v-slot="{ field }">
                <input v-bind="field" type="checkbox" id="estModulaire" class="form-check-input"
                  @change="onEstModulaireChange" />
              </Field>
              <label class="form-check-label fw-semibold" for="estModulaire">
                Ce support est modulaire (divisé en plusieurs modules)
              </label>
            </div>
          </div>
        </div>


         <!-- Section Modules -->
        <div v-if="estModulaire" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-3 bg-primary">
                <div class="col-md-7 ps-4">
                  <h4 class="fs-4 text-white mb-0">Modules du support</h4>
                </div>
                <div class="col-md-5 pe-4">
                  <button class="btn btn-success float-end" type="button" @click="addNewModule">
                    <i class="fa fa-plus-circle me-1"></i> Ajouter un module
                  </button>
                </div>
              </div>
              <div class="p-4">
                <div v-for="(module, index) in addedModules" :key="module.tempId">
                  <div class="row mt-3 align-items-start">
                    <div class="col-md-3">
                      <label class="d-block text-black fw-semibold mb-2">
                        Code module <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${index}].code`" v-slot="{ field }">
                        <input v-bind="field" type="text" class="form-control shadow-none" placeholder="Ex: MOD-001"
                          maxlength="50" />
                      </Field>
                      <ErrorMessage :name="`modulesAdded[${index}].code`" class="text-danger" />
                    </div>
                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Titre du module <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${index}].titre`" v-slot="{ field }">
                        <input v-bind="field" type="text" class="form-control shadow-none"
                          placeholder="Titre du module" />
                      </Field>
                      <ErrorMessage :name="`modulesAdded[${index}].titre`" class="text-danger" />
                    </div>
                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Fichier du module (PDF) <span class="text-danger">*</span>
                      </label>
                      <input type="file" accept="application/pdf" @change="(e) => onModuleFichierChange(index, e)"
                        class="form-control shadow-none" />
                      <small v-if="module.fichierName" class="text-success d-block mt-1">
                        {{ module.fichierName }}
                      </small>
                    </div>
                    <div class="col-md-1 d-flex align-items-start pt-4">
                      <button type="button" class="btn btn-danger btn-sm" @click="removeModule(index)"
                        :disabled="addedModules.length === 1">
                        <i class="flaticon-delete"></i>
                      </button>
                    </div>
                  </div>
                  <div class="row mt-3">
                    <div class="col-md-12">
                      <label class="d-block text-black fw-semibold mb-2">Description du module</label>
                      <Field :name="`modulesAdded[${index}].description`" v-slot="{ field }">
                        <textarea v-bind="field" class="form-control shadow-none" rows="3"
                          placeholder="Description optionnelle du module..."></textarea>
                      </Field>
                    </div>
                  </div>
                  <hr class="my-4" v-if="index < addedModules.length - 1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- fin row gx-2 -->

        <!-- ===================== COLLECTION DE VIDÉOS ===================== -->
        <div class="border border-warning mb-10 mt-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-warning">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">
                <i class="fa fa-video me-2"></i> Collection de vidéos
              </h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button class="btn btn-light" type="button" @click="addVideo">
                  <i class="fa fa-plus-circle me-1 fs-12"></i>
                  Ajouter une vidéo
                </button>
              </div>
            </div>
          </div>
          <div class="p-3">
            <div v-if="videos.length === 0" class="text-center text-muted py-3">
              Aucune vidéo ajoutée. Cliquez sur "Ajouter une vidéo" pour commencer.
            </div>
            <div v-for="(video, index) in videos" :key="index">
              <div class="row mt-10" v-if="index === 0">
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-10">
                    Titre de la vidéo <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-4">
                  <label class="d-block text-black fw-semibold mb-10">
                    URL / Fichier vidéo <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-3">
                  <label class="d-block text-black fw-semibold mb-10">Description</label>
                </div>
                <div class="col-md-1">
                  <label class="d-block text-black fw-semibold mb-10">Action</label>
                </div>
              </div>
              <div class="row mb-10 align-items-start">
                <div class="col-md-4">
                  <input type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Ex: Introduction au chapitre 1" v-model="video.titre" />
                </div>
                <div class="col-md-4">
                  <div class="d-flex gap-2 mb-1">
                    <div class="form-check">
                      <input class="form-check-input" type="radio" :name="`videoSource_${index}`" value="url"
                        v-model="video.sourceType" />
                      <label class="form-check-label">URL</label>
                    </div>
                    <div class="form-check ms-2">
                      <input class="form-check-input" type="radio" :name="`videoSource_${index}`" value="fichier"
                        v-model="video.sourceType" />
                      <label class="form-check-label">Fichier</label>
                    </div>
                  </div>
                  <input v-if="video.sourceType === 'url'" type="url"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="https://youtube.com/..."
                    v-model="video.url" :required="video.sourceType === 'url'" />
                  <div v-else>
                    <input type="file" accept="video/*" class="form-control shadow-none fs-md-15 text-black"
                      @change="(e) => onVideoFichierChange(index, e)" />
                    <small v-if="video.fichierName" class="text-success d-block mt-1">
                      {{ video.fichierName }}
                    </small>
                  </div>
                </div>
                <div class="col-md-3">
                  <textarea class="form-control shadow-none fs-md-15 text-black" rows="2"
                    placeholder="Description courte (optionnel)" v-model="video.description"></textarea>
                </div>
                <div class="col-md-1 pt-1">
                  <button class="btn btn-danger transition border-0 pb-2 ps-8 pe-8" type="button"
                    @click="removeVideo(index)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                      viewBox="0 0 16 16" class="text-white">
                      <path
                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                      <path fill-rule="evenodd"
                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                    </svg>
                  </button>
                </div>
              </div>
              <hr v-if="index < videos.length - 1" class="my-2" />
            </div>
          </div>
        </div>
        <!-- =================== FIN COLLECTION DE VIDÉOS =================== -->

        <!-- ===================== COLLECTION DE TEXTES ===================== -->
        <div class="border border-info mb-10 mt-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-info">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">
                <i class="fa fa-file-alt me-2"></i> Collection de textes
              </h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button class="btn btn-light" type="button" @click="addTexte">
                  <i class="fa fa-plus-circle me-1 fs-12"></i>
                  Ajouter un texte
                </button>
              </div>
            </div>
          </div>
          <div class="p-3">
            <div v-if="textes.length === 0" class="text-center text-muted py-3">
              Aucun texte ajouté. Cliquez sur "Ajouter un texte" pour commencer.
            </div>
            <div v-for="(texte, index) in textes" :key="index">
              <div class="row mt-10" v-if="index === 0">
                <div class="col-md-3">
                  <label class="d-block text-black fw-semibold mb-10">
                    Titre <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-2">
                  <label class="d-block text-black fw-semibold mb-10">Type</label>
                </div>
                <div class="col-md-6">
                  <label class="d-block text-black fw-semibold mb-10">
                    Fichier <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-1">
                  <label class="d-block text-black fw-semibold mb-10">Action</label>
                </div>
              </div>
              <div class="row mb-10 align-items-start">
                <div class="col-md-3">
                  <input type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Ex: Résumé du chapitre 1" v-model="texte.titre" />
                </div>
                <div class="col-md-2">
                  <select class="form-select shadow-none fs-md-15 text-black" v-model="texte.type">
                    <option value="note">Note</option>
                    <option value="resume">Résumé</option>
                    <option value="definition">Définition</option>
                    <option value="exercice">Exercice</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <input type="file" class="form-control shadow-none fs-md-15 text-black" accept=".pdf,.doc,.docx,.txt"
                    @change="handleTexteFileUpload($event, index)" />
                  <small v-if="texte.fichierName" class="text-success d-block mt-1">
                    {{ texte.fichierName }}
                  </small>
                </div>
                <div class="col-md-1 pt-1">
                  <button class="btn btn-danger transition border-0 pb-2 ps-8 pe-8" type="button"
                    @click="removeTexte(index)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                      viewBox="0 0 16 16" class="text-white">
                      <path
                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                      <path fill-rule="evenodd"
                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                    </svg>
                  </button>
                </div>
              </div>
              <hr v-if="index < textes.length - 1" class="my-2" />
            </div>
          </div>
        </div>
        <!-- =================== FIN COLLECTION DE TEXTES =================== -->

        
       
        <!-- Boutons d'action -->
        <div class="row mt-5">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit" :disabled="isSubmitting">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Création en cours...' : 'Créer le support de cours' }}
              </button>
              <router-link to="/liste-support" class="btn btn-danger transition border-0 lh-1 fw-medium">
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i> Annuler
              </router-link>
            </div>
          </div>
        </div>

      </Form>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, onMounted, ref, computed, reactive } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';
import { useAuthStore } from "@/services/auth";

interface Option { value: number; label: string; }
interface ModuleTemp { tempId: number; code: string; titre: string; description?: string; fichier?: File | null; fichierName?: string; }

// ---- Nouvelles interfaces ----
interface VideoItem {
  titre: string;
  sourceType: 'url' | 'fichier';
  url: string;
  fichier: File | null;
  fichierName: string;
  description: string;
}
interface TexteItem {
  titre: string;
  type: 'note' | 'resume' | 'definition' | 'exercice' | 'autre';
  fichier: File | null;
  fichierName?: string;
}

export default defineComponent({
  name: "AddSupportCours",
  components: { Form, Field, ErrorMessage, Multiselect },

  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const supportForm = ref<any>(null);

    const isProfesseur = computed(() => !!authStore.user?.professeur?.id);

    // États du formulaire
    const fichier = ref<File | null>(null);
    const fichierName = ref("");
    const estModulaire = ref(false);
    const addedModules = ref<ModuleTemp[]>([]);
    const isSubmitting = ref(false);
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    let tempModuleId = 1;

    // Options des selects
    const niveauxOptions = ref<Option[]>([]);
    const filieresOptions = ref<Option[]>([]);
    const matieresOptions = ref<Option[]>([]);
    const isLoadingAssignments = ref(true);
    const isLoadingFilieres = ref(false);
    const isLoadingMatieres = ref(false);

    //fichier 
    const fileInput = ref<HTMLInputElement | null>(null);
    const imagePreview = ref<string | null>(null);
    const imageFile = ref<File | null>(null);  // null au lieu de undefined


    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const onFilePrinChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];

      if (file) {
        console.log("✅ Image sélectionnée :", file.name);

        imageFile.value = file;
        supportForm.value?.setFieldValue('urlImage', file);   // ← Très important !

        const reader = new FileReader();
        reader.onload = (e) => {
          imagePreview.value = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };

    const removeImage = () => {
      imagePreview.value = null;
      imageFile.value = null;

      // Important : permet de re-sélectionner la même image après suppression
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };
    // Assignations professeur
    const professeurAssignments = ref<any>(null);


    const handleTexteFileUpload = (event: Event, index: number) => {
      const target = event.target as HTMLInputElement;

      if (target.files?.[0] && textes[index]) {
        textes[index].fichier = target.files[0];
        textes[index].fichierName = target.files[0].name;
      }
    };

    // ---- Collections vidéos et textes ----
    const videos = reactive<VideoItem[]>([]);
    const textes = reactive<TexteItem[]>([]);

    const addVideo = () => {
      videos.push({
        titre: '',
        sourceType: 'url',
        url: '',
        fichier: null,
        fichierName: '',
        description: '',
      });
    };

    const removeVideo = (index: number) => {
      videos.splice(index, 1);
    };

    const onVideoFichierChange = (index: number, e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0] && videos[index]) {
        videos[index].fichier = input.files[0];
        videos[index].fichierName = input.files[0].name;
      }
    };

    const addTexte = () => {
      textes.push({
        titre: '',
        type: 'note',
        fichier: null,
        fichierName: '',
      });
    };

    const removeTexte = (index: number) => {
      textes.splice(index, 1);
    };
    // ---- Fin collections ----

    // Schéma de validation
    const supportSchema = Yup.object().shape({
      titre: Yup.string().required("Le titre est obligatoire"),
      urlImage: Yup.mixed().nullable(), // ou .required() si obligatoire
      niveau: Yup.number().required("Le niveau est obligatoire"),
      filiere: Yup.number().required("La filière est obligatoire"),
      matiere: Yup.number().required("La matière est obligatoire"),
      nombreHeures: Yup.number().required("Le nombre d'heures est obligatoire").min(1).max(1000),
      fichier: Yup.mixed().required("Le fichier principal est obligatoire"),
      estModulaire: Yup.boolean(),
      modulesAdded: Yup.array().when("estModulaire", {
        is: true,
        then: (schema) => schema.of(Yup.object().shape({
          code: Yup.string().required("Le code du module est obligatoire"),
          titre: Yup.string().required("Le titre du module est obligatoire"),
          fichier: Yup.mixed().required("Le fichier du module est obligatoire"),
        })).min(1, "Au moins un module requis"),
        otherwise: (schema) => schema.nullable(),
      }),
    });

    const handleInvalidSubmit = () => {
      error("Veuillez corriger les erreurs du formulaire");
    };



    // Gestion fichier principal
    const onFichierChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0]) {
        fichier.value = input.files[0];
        fichierName.value = input.files[0].name;
        supportForm.value?.setFieldValue("fichier", input.files[0]);
      }
    };

    const onModuleFichierChange = (index: number, e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.files?.[0] && addedModules.value[index]) {
        addedModules.value[index].fichier = input.files[0];
        addedModules.value[index].fichierName = input.files[0].name;
        supportForm.value?.setFieldValue(`modulesAdded[${index}].fichier`, input.files[0]);
      }
    };



    const onEstModulaireChange = (e: Event) => {
      const checked = (e.target as HTMLInputElement).checked;
      estModulaire.value = checked;
      if (checked && addedModules.value.length === 0) addNewModule();
      if (!checked) addedModules.value = [];
    };

    const addNewModule = () => {
      addedModules.value.push({
        tempId: tempModuleId++,
        code: "",
        titre: "",
        description: "",
        fichier: null,
        fichierName: "",
      });
    };

    const removeModule = (index: number) => {
      addedModules.value.splice(index, 1);
    };

    // Chargement des assignations (prof ou admin)
    const fetchAssignments = async () => {
      isLoadingAssignments.value = true;
      try {
        if (isProfesseur.value) {
          const res = await ApiService.get('/supports/professeur/assignments');
          professeurAssignments.value = res.data.data;
          niveauxOptions.value = professeurAssignments.value.niveaux.map((n: any) => ({
            value: n.id, label: n.libelle
          }));
        } else {
          // Admin → tous les niveaux
          const res = await ApiService.get('/all/niveaux?limit=0');
          niveauxOptions.value = res.data.data.data.map((n: any) => ({
            value: n.id, label: n.libelle
          }));
        }
      } catch (err) {
        error("Impossible de charger les niveaux");
      } finally {
        isLoadingAssignments.value = false;
      }
    };

    const onNiveauChange = async (val: any) => {
      selectedNiveau.value = val;
      selectedFiliere.value = null;
      filieresOptions.value = [];
      matieresOptions.value = [];
      supportForm.value?.setFieldValue('filiere', null);
      supportForm.value?.setFieldValue('matiere', null);

      if (val && isProfesseur.value && professeurAssignments.value) {
        const filieres = professeurAssignments.value.filieres.filter((f: any) => f.niveauId === val);
        filieresOptions.value = filieres.map((f: any) => ({ value: f.id, label: f.libelle }));
      } else if (val) {
        isLoadingFilieres.value = true;
        const res = await ApiService.get(`/filiereNiveauMatieres?niveau=${val}&limit=0`);
        const uniqueFilieres = Array.from(new Set(res.data.data.data.map((i: any) => i.filiere.id)))
          .map(id => res.data.data.data.find((i: any) => i.filiere.id === id).filiere);
        filieresOptions.value = uniqueFilieres.map((f: any) => ({ value: f.id, label: f.libelle }));
        isLoadingFilieres.value = false;
      }
    };

    const onFiliereChange = async (val: any) => {
      selectedFiliere.value = val;
      matieresOptions.value = [];
      supportForm.value?.setFieldValue('matiere', null);

      if (val && selectedNiveau.value) {
        isLoadingMatieres.value = true;
        if (isProfesseur.value && professeurAssignments.value) {
          const matieres = professeurAssignments.value.matieres.filter(
            (m: any) => m.niveauId === selectedNiveau.value && m.filiereId === val
          );
          matieresOptions.value = matieres.map((m: any) => ({ value: m.id, label: m.libelle }));
        } else {
          const res = await ApiService.get(`/filiereNiveauMatieres?niveau=${selectedNiveau.value}&filiere=${val}&limit=0`);
          matieresOptions.value = res.data.data.data.map((i: any) => ({
            value: i.id,
            label: i.matiere.libelle
          }));
        }
        isLoadingMatieres.value = false;
      }
    };

    const addSupportCours = async (values: any) => {
      isSubmitting.value = true;

      try {
        const formData = new FormData();

        console.log("🚀 === DÉBUT DE LA SOUMISSION ===");

        // Vérification détaillée de l'image
        console.log("🔍 État de imageFile.value juste avant envoi :", imageFile.value);

        if (imageFile.value) {
          formData.append('urlImage', imageFile.value);
          console.log("✅ urlImage ajouté au FormData :", imageFile.value.name);
        } else {
          console.warn("⚠️ Aucune image de couverture sélectionnée");
        }
        formData.append('nombreHeures', values.nombreHeures);
        formData.append('prerequis', values.prerequis || '');
        formData.append('description', values.description);
        formData.append('titre', values.titre);
        formData.append('auteur', values.auteur || '');
        formData.append('estModulaire', estModulaire.value.toString());
        formData.append('filiereNiveauMatiereId', values.matiere.toString());

        if (fichier.value) formData.append('fichier', fichier.value);

        if (estModulaire.value && values.modulesAdded && values.modulesAdded.length > 0) {
          formData.append('modulesData', JSON.stringify(values.modulesAdded.map((m: any) => ({
            code: m.code?.trim(),
            titre: m.titre?.trim(),
            description: m.description?.trim() || null
          }))));

          values.modulesAdded.forEach((m: any, i: number) => {
            if (addedModules.value[i]?.fichier) {
              formData.append(`moduleFichier_${i}`, addedModules.value[i].fichier!);
            }
          });
        }


        console.log("📦 CONTENU COMPLET DU FormData :");
        let urlImagePresent = false;
        for (const [key, value] of formData.entries()) {
          if (key === 'urlImage' && value instanceof File) {
            urlImagePresent = true;
            console.log(`   ✅ ${key} → [FILE] ${value.name}`);
          } else if (value instanceof File) {
            console.log(`   ${key} → [FILE] ${value.name}`);
          } else {
            console.log(`   ${key} → ${value}`);
          }
        }

        if (!urlImagePresent) {
          console.warn("⚠️ urlImage n'a PAS été ajouté dans le FormData !");
        } else {
          console.log("🎉 urlImage est bien présent dans le FormData");
        }

        // ---- Sérialisation des vidéos ----
        if (videos.length > 0) {
          const videosMeta = videos.map((v, i) => ({
            titre: v.titre?.trim() || '',
            sourceType: v.sourceType,
            url: v.sourceType === 'url' ? v.url?.trim() : null,
            description: v.description?.trim() || null,
          }));

          formData.append('videosData', JSON.stringify(videosMeta));

          // Envoi des fichiers vidéo
          videos.forEach((v, i) => {
            if (v.sourceType === 'fichier' && v.fichier) {
              formData.append(`videoFichier_${i}`, v.fichier);
            }
          });
        }

        if (videos.some(v =>
          !v.titre?.trim() ||
          (v.sourceType === 'url' && !v.url?.trim()) ||
          (v.sourceType === 'fichier' && !v.fichier)
        )) {
          error("Veuillez remplir correctement toutes les vidéos (titre + URL ou fichier)");
          isSubmitting.value = false;
          return;
        }


        // ==== SÉRIALISATION DES TEXTES (CORRIGÉ) ====
        if (textes.length > 0) {
          const textesMeta = textes.map((t, i) => ({
            titre: t.titre?.trim(),
            type: t.type,
            description: '', // tu peux ajouter un champ description si besoin
            fichierIndex: i,   // important pour mapper le fichier
          }));

          formData.append('textesData', JSON.stringify(textesMeta));

          // Envoi des fichiers textes
          textes.forEach((t, i) => {
            if (t.fichier) {
              formData.append(`texteFichier_${i}`, t.fichier);
            }
          });
        }


        const response = await ApiService.post('/supports', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        success("Support de cours créé avec succès !");
        router.push('/liste-support');

      } catch (err: any) {


        error(err.response?.data?.message || "Erreur lors de la création");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      fetchAssignments();
      if (estModulaire.value) addNewModule();
    });

    return {
      supportSchema,
      supportForm,
      niveauxOptions,
      filieresOptions,
      matieresOptions,
      fichierName,
      estModulaire,
      addedModules,
      isSubmitting,
      isLoadingAssignments,
      isLoadingFilieres,
      isLoadingMatieres,
      selectedNiveau,
      selectedFiliere,
      isProfesseur,
      handleInvalidSubmit,
      onFichierChange,
      onModuleFichierChange,
      onEstModulaireChange,
      addNewModule,
      removeModule,
      onNiveauChange,
      onFiliereChange,
      addSupportCours,

      // === Variables et méthodes pour l'image ===
      fileInput,
      imagePreview,        // ← AJOUTÉ
      imageFile,
      triggerFileInput,
      onFilePrinChange,
      removeImage,

      // Collections
      videos,
      textes,
      addVideo,
      removeVideo,
      onVideoFichierChange,
      addTexte,
      removeTexte,
      handleTexteFileUpload,
    };
  },
});
</script>
<style scoped>
.preview-card {
  width: 100%;
  height: 160px;
  /* un peu plus grand */
  border: 2px dashed #4caf50;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  background: #f8f8f8;
  position: relative;
}

.preview-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}

.delete-btn:hover {
  background: #dc3545;
}
</style>
